/**
 * The satellite admin routes, as the console calls them (2026-09-29-gpu-and-regional-app-pools,
 * WS-7). With them a data plane admin does these acts on a satellite of a data plane:
 *
 * - sets it up on a cloud and in a region, with the machine types its cluster may run;
 * - follows its setup job step by step;
 * - changes its machine types;
 * - removes it.
 *
 * Plan 2026-10-24-aws-dp-identity-and-lifecycle (WS-14, contract 3, D-22, D-25, D-26) adds two reads:
 * the latest job of one satellite (`getDpSatelliteJob`) and the regions and machine types of one
 * cloud (`getDpSatelliteRegions`). A satellite runs on `gcp` or `aws` (D-17, D-30, R-043).
 *
 * WS-29 of the same plan (contract 4, D-29, R-044) adds the satellite region policy of one data plane:
 * the read `getDpSatelliteRegionPolicy` and the write `putDpSatelliteRegionPolicy`. The policy names the
 * regions, per cloud, where a new satellite of the data plane may run.
 *
 * The console calls site-admin-api's proxy under `/v1/admin` (WS-6), never provisioning-svc's
 * `/v1/data-planes` paths. Every path, schema and permission is imported from
 * `@zion/sdk-contracts` (`dp-satellites.ts`, WS-1); this module restates none of them.
 *
 * Every region name an answer carries must fit the region form of its cloud
 * ({@link satelliteRegionFits}). The regions read and the satellites read refuse an answer with a
 * region outside the form ({@link SatelliteRegionFormError}), so no server text in a region field
 * reaches a label, a heading or a sentence of the card.
 *
 * Permissions, as the server enforces them (R-007, R-044): `GET` requires `cp:dp:read`
 * (`DP_SATELLITES_READ_PERMISSION`), `PUT` and `DELETE` require `cp:dp:update`
 * (`DP_SATELLITES_WRITE_PERMISSION`). The policy routes follow the same rule. The Regions section reads the write permission at its point of
 * use, with no resource id, the way `MachineTypesEditor` reads `DP_UPDATE_PERMISSION`.
 */
import {
  DP_SATELLITES_ADMIN_PATH,
  DP_SATELLITE_ADMIN_PATH,
  DP_SATELLITE_JOB_ADMIN_PATH,
  DP_SATELLITE_REGIONS_ADMIN_PATH,
  DP_SATELLITE_REGION_POLICY_ADMIN_PATH,
  SATELLITE_PROVIDERS,
  appPoolRegionSchema,
  dpSatelliteInUseErrorSchema,
  dpSatelliteJobSchema,
  dpSatelliteRegionPolicyResponseSchema,
  dpSatelliteRegionsResponseSchema,
  dpSatelliteSchema,
  dpSatellitesAdminResponseSchema,
} from '@zion/sdk-contracts';
import type {
  DpSatellite,
  DpSatelliteInUseError,
  DpSatelliteJob,
  DpSatelliteRegionPolicyResponse,
  DpSatelliteRegionsResponse,
  DpSatellitesAdminResponse,
  PutDpSatelliteRegionPolicyRequest,
  PutDpSatelliteRequest,
  SatelliteProvider,
} from '@zion/sdk-contracts';
import { maskCredentialShapes } from '../platform/credential-mask';
import { ApiError, TokenRefreshTimeoutError, apiFetch } from '../platform/http/client';

/**
 * The time bound of one satellite write (`PUT` or `DELETE`), in ms: 45 s, the bound of the job remedy
 * writes (`REMEDY_TIMEOUT_MS`). A write that has not answered by then rejects with
 * {@link SatelliteWriteTimeoutError}, so a stalled request never holds a panel locked (Corridor default 9
 * of plan 2026-10-24-aws-dp-identity-and-lifecycle).
 */
export const SATELLITE_WRITE_TIMEOUT_MS = 45_000;

/**
 * The rejection of a satellite write that ran out of {@link SATELLITE_WRITE_TIMEOUT_MS}. Its `message` is
 * the sentence the panel shows. The server may have applied the write, so the outcome is unknown: the
 * card reads its table again before the operator decides.
 */
export class SatelliteWriteTimeoutError extends Error {
  constructor() {
    super(
      `The request did not finish in ${SATELLITE_WRITE_TIMEOUT_MS / 1000} seconds. The change may still have applied. ` +
        'The table reads again, so check it before you try again.',
    );
    this.name = 'SatelliteWriteTimeoutError';
  }
}

/**
 * The time bound of one satellite read that its caller does not bound, in ms: 25 s, the bound of the job
 * poll (`SATELLITE_JOB_READ_TIMEOUT_MS` of `use-satellite-job.ts`). The reads of the satellite table, the
 * satellite regions and the satellite region policy take it when the caller passes no signal (review rows
 * WS-29-R45, WS-29-R26 and WS-29-R15). A read that has not answered by then rejects with
 * {@link SatelliteReadTimeoutError}, so a stalled request ends in an error state with Try again and never
 * holds a card, an editor or the setup on its loading skeleton (Corridor default 9 of plan
 * 2026-10-24-aws-dp-identity-and-lifecycle). A caller that passes its own signal keeps its own bound.
 */
export const SATELLITE_READ_TIMEOUT_MS = 25_000;

/**
 * The rejection of a satellite read that ran out of {@link SATELLITE_READ_TIMEOUT_MS}. Its `message` is the
 * sentence the error state shows. A read writes nothing, so a Try again is always safe.
 */
export class SatelliteReadTimeoutError extends Error {
  constructor() {
    super(`The read did not finish in ${SATELLITE_READ_TIMEOUT_MS / 1000} seconds. Press Try again to read it again.`);
    this.name = 'SatelliteReadTimeoutError';
  }
}

/**
 * `true` when a failed satellite write may still have applied (review row WS-14-R26):
 *
 * - a failure that never reached a response or whose answer did not parse under the row contract: a
 *   network failure, {@link SatelliteWriteTimeoutError}, a `ZodError`;
 * - an `ApiError` with a 2xx status: the server answered success with a body that is not JSON
 *   (`parseBody` of the client), so the write applied and the answer was lost;
 * - an `ApiError` with status 499 (a proxy's "client closed request"): the server may have run the write;
 * - an `ApiError` with a 5xx status.
 *
 * `false` for a refusal that changed nothing: any other 4xx `ApiError`, and
 * `TokenRefreshTimeoutError`, whose request was not sent again after its 401. The panels read the table,
 * or the job, again after a `true`. Pure.
 */
export function satelliteWriteOutcomeUnknown(error: unknown): boolean {
  if (error instanceof TokenRefreshTimeoutError) return false;
  if (!(error instanceof ApiError)) return true;
  return error.status < 400 || error.status === 499 || error.status >= 500;
}

/** The body members that carry server text: Nest's `message` (a string or the strict pipe's list) and OAuth's `error_description`. */
const SERVER_TEXT_MEMBERS = ['message', 'error_description'] as const;

/**
 * `error` with the server text of an `ApiError` body masked whole by `maskCredentialShapes` (letter runs
 * included), as a new `ApiError` of the same status; any other value unchanged.
 *
 * Callers: every read and write of this module, on its rejection. `apiErrorMessage` and `displayMessage`
 * cap the text at 4000 code points before they redact, so a credential that the cap cuts would show as a
 * fragment. This mask reads the whole body first (review rows WS-14-R27 and WS-14-R28). Each other body
 * member, `code`, `region` and `nodeGroupCount` of the `SATELLITE_IN_USE` refusal among them, is kept.
 * Pure.
 */
export function maskedSatelliteError(error: unknown): unknown {
  if (!(error instanceof ApiError)) return error;
  const body = error.body;
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return error;
  const masked: Record<string, unknown> = { ...body };
  const mask = (text: string): string => maskCredentialShapes(text, { letterRuns: 'any' });
  for (const member of SERVER_TEXT_MEMBERS) {
    const value = masked[member];
    if (typeof value === 'string') masked[member] = mask(value);
    else if (Array.isArray(value)) masked[member] = value.map((item: unknown) => (typeof item === 'string' ? mask(item) : item));
  }
  return new ApiError(error.status, masked);
}

/** Rethrow `error` through {@link maskedSatelliteError}: the rejection handler of every request here. */
function rethrowMasked(error: unknown): never {
  throw maskedSatelliteError(error);
}

/** The result of a contract schema's `safeParse`: the parsed value, or the error that refused it. */
type ParseResult<T> = { readonly success: true; readonly data: T } | { readonly success: false; readonly error: Error };

/**
 * The value of `parsed`, the `safeParse` of a server answer against its contract schema; on a refusal,
 * the schema's own error (a `ZodError`), thrown with its `message` masked whole by `maskCredentialShapes`
 * (letter runs included).
 *
 * Why. The strict schemas name each key they refuse in the message, so a server answer with an extra
 * key whose name carries a credential puts that credential in the message. `useApiResource` hands the
 * message to `apiErrorMessage`, which caps it at 4000 code points before it redacts, so a credential that
 * the cap cuts would show as a fragment. This mask reads the whole message first (review row
 * WS-14-P2-R12). The error keeps its class, so a caller still tells a refused answer by its `name`. Its
 * `issues` stay as the schema wrote them: no surface of the console renders them.
 *
 * Callers: every answer parse of this module (`getDpSatellites`, `putDpSatellite`, `getDpSatelliteJob`,
 * `getDpSatelliteRegions`, and `getDpSatelliteRegionPolicy` and `putDpSatelliteRegionPolicy` through
 * `policyAnswerOf`).
 */
function answerOf<T>(parsed: ParseResult<T>): T {
  if (parsed.success) return parsed.data;
  parsed.error.message = maskCredentialShapes(parsed.error.message, { letterRuns: 'any' });
  throw parsed.error;
}

/*
 * One satellite write, bounded by `SATELLITE_WRITE_TIMEOUT_MS` as a whole, by the mechanism of
 * `postRemedy` in `provisioningJobs.api.ts`. The signal aborts the fetch, and its abort also rejects
 * the race, so the bound covers a token refresh after a 401, which takes no signal. The shared refresh
 * itself has its own bound (`REFRESH_TIMEOUT_MS` in `client.ts`), so a refresh that never answers
 * frees the shared refresh slot for the next 401. Any rejection after the signal aborted is a
 * `SatelliteWriteTimeoutError`; every other failure rejects unchanged, so a 409 keeps the server's
 * coded body. The abort listener goes when the call settles.
 */
async function boundedWrite(path: string, method: 'PUT' | 'DELETE', body?: unknown): Promise<unknown> {
  const signal = AbortSignal.timeout(SATELLITE_WRITE_TIMEOUT_MS);
  const settled = new AbortController();
  const bound = new Promise<never>((_, reject) => {
    signal.addEventListener('abort', () => reject(new SatelliteWriteTimeoutError()), { once: true, signal: settled.signal });
  });
  try {
    return await Promise.race([
      apiFetch<unknown>(path, { method, body: body === undefined ? undefined : JSON.stringify(body), signal }).catch(rethrowMasked),
      bound,
    ]);
  } catch (error) {
    if (signal.aborted) throw new SatelliteWriteTimeoutError();
    throw error;
  } finally {
    settled.abort();
  }
}

/**
 * The satellites collection path of one data plane: {@link DP_SATELLITES_ADMIN_PATH} with `:id`
 * replaced by the URI-encoded `dpId`. It is also the cache key the Regions section's
 * `useApiResource` reads, so it carries no nonce and is stable per data plane. Pure.
 */
export function dpSatellitesAdminPath(dpId: string): string {
  return DP_SATELLITES_ADMIN_PATH.replace(':id', encodeURIComponent(dpId));
}

/**
 * The path of one data plane's satellite in one region: {@link DP_SATELLITE_ADMIN_PATH} with `:id`
 * and `:region` replaced.
 *
 * `region` must match `APP_POOL_REGION_PATTERN`. A region outside the grammar throws a `ZodError`
 * before any string is built, so an operator-typed value that is not a region name never reaches a
 * URL. A region inside the grammar holds only lower-case letters, digits and hyphens, and the
 * function still URI-encodes it. Pure.
 */
export function dpSatelliteAdminPath(dpId: string, region: string): string {
  const checked = appPoolRegionSchema.parse(region);
  return DP_SATELLITE_ADMIN_PATH.replace(':id', encodeURIComponent(dpId)).replace(
    ':region',
    encodeURIComponent(checked),
  );
}

/**
 * Thrown when a satellite answer parses under its contract schema and still names a different data
 * plane or region from the one the console asked about.
 *
 * The contract checks an answer against itself: every member of a `GET` answer carries the answer's
 * own `dataPlaneId`. It cannot check the answer against the request, so the console does. Without the
 * check, a `GET` answer for data plane B would render in data plane A's Regions section, and its Edit
 * and Disable actions would then write A's paths with B's regions. `message` names what was asked and
 * what came back, and the section renders it through `displayMessage` as its error state. `asked` and
 * `answered` carry the two values, lower-cased when they are data plane ids.
 */
export class SatelliteAnswerMismatchError extends Error {
  constructor(
    /**
     * `dataPlaneId`, `region`, `provider` (the regions read) or `policy` (a policy `PUT` whose answer stores
     * other lists than the body): the member that disagrees.
     */
    readonly member: 'dataPlaneId' | 'region' | 'provider' | 'policy',
    /** The value the console asked about. */
    readonly asked: string,
    /** The value the server answered. */
    readonly answered: string,
  ) {
    super(`The server answered for ${member} ${answered} when ${asked} was asked for, so the answer is not shown.`);
    this.name = 'SatelliteAnswerMismatchError';
  }
}

/** The direction words of a cloud region name: `us-east4`, `europe-west1`, `us-east-1`, `ap-southeast-2`. */
const REGION_DIRECTION = '(?:central|east|west|north|south|northeast|northwest|southeast|southwest)';

/**
 * The geography words that start a Google Cloud region: every prefix of provisioning-svc's
 * `SATELLITE_SLOT_REGIONS` (`africa-south1` to `us-west4`). A slot region outside them needs this list to
 * grow in the same change.
 */
const GCP_REGION_GEOGRAPHY = '(?:africa|asia|australia|europe|me|northamerica|southamerica|us)';

/** The geography codes that start an AWS commercial or GovCloud region (`us-east-1`, `ap-southeast-2`, `us-gov-west-1`). */
const AWS_REGION_GEOGRAPHY = '(?:af|ap|ca|eu|il|me|mx|sa|us)';

/**
 * The region form of each cloud. A Google Cloud region is a geography word, a hyphen, a direction word
 * and 1 or 2 digits (`us-east4`, `northamerica-northeast1`). An AWS region is a geography code, an
 * optional `-gov`, a hyphen, a direction word, a hyphen and 1 or 2 digits (`us-east-1`, `us-gov-west-1`).
 * Every word of a form comes from a closed list, so a region text carries no letter of free choice: a
 * credential in a region field cannot reach a heading or a sentence, not even as a short prefix (review
 * row WS-14-R33). Each form is a strict subset of `APP_POOL_REGION_PATTERN`.
 */
const SATELLITE_REGION_FORMS: Readonly<Record<SatelliteProvider, RegExp>> = {
  gcp: new RegExp(`^${GCP_REGION_GEOGRAPHY}-${REGION_DIRECTION}[0-9]{1,2}$`),
  aws: new RegExp(`^${AWS_REGION_GEOGRAPHY}(?:-gov)?-${REGION_DIRECTION}-[0-9]{1,2}$`),
};

/**
 * `true` when `region` fits the region form of `provider` ({@link SATELLITE_REGION_FORMS}). The reads of
 * this module refuse an answer whose region does not fit, and the card shows a region name only after
 * this check. A text that fits is built from closed word lists and digits, so a credential that the
 * server text carries in a region field cannot reach the screen. Pure.
 */
export function satelliteRegionFits(provider: SatelliteProvider, region: string): boolean {
  return SATELLITE_REGION_FORMS[provider].test(region);
}

/**
 * Thrown when an answer parses under its contract schema and still carries a region name outside the
 * region form of its cloud ({@link satelliteRegionFits}). The message names the cloud and never the
 * region, because the region is the server text the check refuses to show. The card renders the
 * message through `displayMessage` as its error state.
 */
export class SatelliteRegionFormError extends Error {
  constructor(
    /** The cloud whose region form the answer broke. */
    readonly provider: SatelliteProvider,
  ) {
    super(`The server answered a ${provider} region name outside the ${provider} region form, so the answer is not shown.`);
    this.name = 'SatelliteRegionFormError';
  }
}

/**
 * `dpId` as Postgres prints a `uuid`: lower case. R-068 has the path schemas lower-case `:id`, so a
 * console on an upper-case URL still receives the lower-case id back, and a comparison lower-cases
 * both sides.
 */
function sameDataPlane(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Every satellite of one data plane, each with the home operator's last reported status (R-021).
 *
 * Callers: the card's table read, with no `init`, and the Retry of the progress panel, which reads the
 * satellite's stored row just before its `PUT` with the signal of its 25-second bound.
 *
 * The body is parsed with `dpSatellitesAdminResponseSchema`, so a body that breaks the contract (a
 * member with a foreign `dataPlaneId`, a repeated region, regions out of order, counts that disagree
 * with the phase) rejects with a `ZodError`. A body that parses but answers for a data plane other
 * than `dpId`, compared in lower case (R-068), rejects with {@link SatelliteAnswerMismatchError}. A row
 * whose region does not fit the region form of its own `provider` rejects with
 * {@link SatelliteRegionFormError}. The section renders a rejection as its error state, and never
 * renders a partial table. An HTTP failure rejects with the client's `ApiError`.
 *
 * Abort, as {@link getDpSatelliteJob} has it: a signal that is already aborted rejects with its reason
 * and issues no request, and a signal that aborts later rejects the read with its reason at once. A
 * call with no signal (the card's table read, the policy editor's read) is bounded by
 * {@link SATELLITE_READ_TIMEOUT_MS} and rejects with {@link SatelliteReadTimeoutError} when the bound ends it.
 */
export async function getDpSatellites(
  dpId: string,
  init: Pick<RequestInit, 'signal'> = {},
): Promise<DpSatellitesAdminResponse> {
  if (init.signal?.aborted === true) throw init.signal.reason;
  const path = dpSatellitesAdminPath(dpId);
  const answer = answerOf(dpSatellitesAdminResponseSchema.safeParse(await boundedRead(path, init)));
  if (!sameDataPlane(answer.dataPlaneId, dpId)) {
    throw new SatelliteAnswerMismatchError('dataPlaneId', dpId.toLowerCase(), answer.dataPlaneId.toLowerCase());
  }
  const unfit = answer.satellites.find((row) => !satelliteRegionFits(row.provider, row.region));
  if (unfit !== undefined) throw new SatelliteRegionFormError(unfit.provider);
  return answer;
}

/**
 * Create or replace the satellite of `dpId` in `region`. The body is the whole selection: `PUT` is
 * replace (R-008: the body carries no region; the path does).
 *
 * Resolves with the stored row, parsed with `dpSatelliteSchema`. The server replaces every entry
 * with the region catalogue's own entry, so the answer is the truth about what was stored and the
 * request body is not. Rejects with a `ZodError` for a `region` outside the grammar (no request is
 * issued) and for an answer that breaks the row contract, with {@link SatelliteAnswerMismatchError}
 * for a row whose `dataPlaneId` (compared in lower case) or `region` is not the one asked for, and with
 * `ApiError` for any HTTP failure:
 * 400 for the data plane's own region, a region that is not a satellite region of the body's
 * provider, a region the catalogue does not hold, or a body the strict pipe refuses; 403 without
 * `cp:dp:update`; 404 for an unknown data plane; 409 for a home and satellite pair the CP refuses,
 * a region that holds a satellite on the other cloud, or a removal of the region that still runs;
 * 422 for a machine type the region does not offer. A write that has not answered in
 * {@link SATELLITE_WRITE_TIMEOUT_MS} rejects with {@link SatelliteWriteTimeoutError}.
 *
 * The stored row carries the satellite's `account`, `vpn` and latest `job` when the producer sends
 * them (contract 2); the setup panel hands the row to the progress panel.
 */
export async function putDpSatellite(
  dpId: string,
  region: string,
  body: PutDpSatelliteRequest,
): Promise<DpSatellite> {
  const path = dpSatelliteAdminPath(dpId, region);
  const row = answerOf(dpSatelliteSchema.safeParse(await boundedWrite(path, 'PUT', body)));
  if (!sameDataPlane(row.dataPlaneId, dpId)) {
    throw new SatelliteAnswerMismatchError('dataPlaneId', dpId.toLowerCase(), row.dataPlaneId.toLowerCase());
  }
  if (row.region !== region) throw new SatelliteAnswerMismatchError('region', region, row.region);
  return row;
}

/**
 * Disable `region` on `dpId`: the server removes the row and, through the XR patch, the satellite
 * cluster. Resolves with nothing on success.
 *
 * Rejects with a `ZodError` for a `region` outside the grammar (no request is issued), and with
 * `ApiError` for any HTTP failure. The failure a caller must render as itself is the 409 whose body
 * is `dpSatelliteInUseErrorSchema`: a node group still names the region, and the satellite is
 * untouched. {@link satelliteInUseOf} reads it. A write that has not answered in
 * {@link SATELLITE_WRITE_TIMEOUT_MS} rejects with {@link SatelliteWriteTimeoutError}.
 */
export async function deleteDpSatellite(dpId: string, region: string): Promise<void> {
  await boundedWrite(dpSatelliteAdminPath(dpId, region), 'DELETE');
}

/**
 * The `SATELLITE_IN_USE` refusal a `DELETE` of `region` answered, or `null` for any other thrown value.
 *
 * It answers the parsed body only for an `ApiError` with status 409 whose body passes
 * `dpSatelliteInUseErrorSchema`, which requires `code` to be `DP_SATELLITE_IN_USE_CODE`
 * (`SATELLITE_IN_USE`), and whose `region` is `region`. A refusal naming another region is not about
 * the region the operator tried to disable, so the console does not render its count as this region's.
 * A 409 with any other body, any other status, a network failure and a non-`ApiError` value all answer
 * `null`, and the caller renders its generic failure sentence. A client keys on `code`, as the
 * contract says. Pure.
 */
export function satelliteInUseOf(error: unknown, region: string): DpSatelliteInUseError | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const parsed = dpSatelliteInUseErrorSchema.safeParse(error.body);
  return parsed.success && parsed.data.region === region ? parsed.data : null;
}

/**
 * The path of the latest job of `dpId`'s satellite in `region`: {@link DP_SATELLITE_JOB_ADMIN_PATH}
 * with `:id` and `:region` replaced, each URI-encoded. It is also the key of the progress panel's
 * poll, so it carries no nonce.
 *
 * `region` must match `APP_POOL_REGION_PATTERN`. A region outside the grammar throws a `ZodError`
 * before any string is built, as {@link dpSatelliteAdminPath} does. Pure.
 */
export function dpSatelliteJobAdminPath(dpId: string, region: string): string {
  const checked = appPoolRegionSchema.parse(region);
  return DP_SATELLITE_JOB_ADMIN_PATH.replace(':id', encodeURIComponent(dpId)).replace(
    ':region',
    encodeURIComponent(checked),
  );
}

/**
 * The latest job of `dpId`'s satellite in `region` (contract 3, D-25): its build, or its removal
 * while the removal runs.
 *
 * Caller: `useSatelliteJob`, the poll of the progress panel. `init.signal` aborts the read; the poll
 * passes the signal of its 25-second bound. The answer is parsed with `dpSatelliteJobSchema`, so a
 * job whose steps break the contract's rules rejects with a `ZodError`. Rejects with a `ZodError`
 * for a `region` outside the grammar (no request is issued), and with `ApiError` for any HTTP
 * failure: 404 for an unknown data plane, a region with no satellite, or a satellite with no job;
 * 403 without `cp:dp:read`.
 *
 * Abort. A signal that is already aborted rejects with its reason before the region is checked, and no
 * request is issued. A signal
 * that aborts later rejects the read with its reason at once, also while `apiFetch` waits on a token
 * refresh that does not answer: the fetch takes the signal, the refresh does not. So the poll's bound
 * always ends the read. A step `reason` in the answer is server text; the progress panel masks it
 * (`maskSatelliteReason` through `satelliteJobRows`) before `displayUntrusted` renders it.
 */
export async function getDpSatelliteJob(
  dpId: string,
  region: string,
  init: Pick<RequestInit, 'signal'> = {},
): Promise<DpSatelliteJob> {
  if (init.signal?.aborted === true) throw init.signal.reason;
  const path = dpSatelliteJobAdminPath(dpId, region);
  return answerOf(dpSatelliteJobSchema.safeParse(await boundedRead(path, init)));
}

/**
 * The body of one `GET` of `path`. With a caller signal in `init`, the read is `apiFetch` under that
 * signal, as {@link untilAborted} bounds it, and every rejection is the caller's. With no caller signal,
 * the read takes the signal of {@link SATELLITE_READ_TIMEOUT_MS}: the signal aborts the fetch and also
 * ends the wait, so the bound covers a token refresh after a 401, which takes no signal. A read that the
 * bound ends rejects with {@link SatelliteReadTimeoutError}; every other failure rejects unchanged, its
 * server text masked ({@link maskedSatelliteError}). The caller checks an already aborted signal first.
 */
async function boundedRead(path: string, init: Pick<RequestInit, 'signal'>): Promise<unknown> {
  if (init.signal !== undefined && init.signal !== null) {
    return untilAborted(apiFetch<unknown>(path, init).catch(rethrowMasked), init.signal);
  }
  const signal = AbortSignal.timeout(SATELLITE_READ_TIMEOUT_MS);
  try {
    return await untilAborted(apiFetch<unknown>(path, { signal }).catch(rethrowMasked), signal);
  } catch (error) {
    if (signal.aborted) throw new SatelliteReadTimeoutError();
    throw error;
  }
}

/**
 * `work`, or a rejection with `signal`'s reason as soon as `signal` aborts, whichever comes first. Every
 * read passes a signal: the caller's, or the bound of {@link boundedRead}. The caller checks an already
 * aborted signal first, because its `abort` event has fired. `work` keeps its own handlers, so a late
 * rejection of it is never unhandled. The listener is removed when `work` settles.
 */
function untilAborted<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abort = (): void => reject(signal.reason);
    signal.addEventListener('abort', abort, { once: true });
    work.then(
      (value) => {
        signal.removeEventListener('abort', abort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', abort);
        reject(error);
      },
    );
  });
}

/**
 * The path of the satellite regions read for `provider`: {@link DP_SATELLITE_REGIONS_ADMIN_PATH} with
 * the query `?provider=<provider>`. It is also the cache key the setup panel's `useApiResource`
 * reads, so a change of cloud reads the other list. Pure.
 */
export function dpSatelliteRegionsAdminPath(provider: SatelliteProvider): string {
  return `${DP_SATELLITE_REGIONS_ADMIN_PATH}?provider=${encodeURIComponent(provider)}`;
}

/**
 * The regions where a satellite may run on `provider`, each with the machine types it may enable
 * (contract 3, D-26). The answer is the same for every data plane.
 *
 * The body is parsed with `dpSatelliteRegionsResponseSchema`, so a repeated region, regions out of
 * order or a region with no machine type rejects with a `ZodError`. A body that parses and echoes
 * another `provider` rejects with {@link SatelliteAnswerMismatchError} (`member: 'provider'`): the
 * setup panel would else offer one cloud's regions under the other cloud's name. A region outside the
 * region form of `provider` rejects with {@link SatelliteRegionFormError}, so no such name reaches an
 * option label or value. Rejects with `ApiError` for any HTTP failure: 400 for a provider the strict
 * pipe refuses, 403 without `cp:dp:read`, 503 when the machine-type source does not answer. A read that
 * has not answered in {@link SATELLITE_READ_TIMEOUT_MS} rejects with {@link SatelliteReadTimeoutError}.
 */
export async function getDpSatelliteRegions(provider: SatelliteProvider): Promise<DpSatelliteRegionsResponse> {
  const answer = answerOf(dpSatelliteRegionsResponseSchema.safeParse(await boundedRead(dpSatelliteRegionsAdminPath(provider), {})));
  if (answer.provider !== provider) throw new SatelliteAnswerMismatchError('provider', provider, answer.provider);
  if (answer.regions.some((r) => !satelliteRegionFits(provider, r.region))) throw new SatelliteRegionFormError(provider);
  return answer;
}

/**
 * The satellite region policy path of one data plane: {@link DP_SATELLITE_REGION_POLICY_ADMIN_PATH} with
 * `:id` replaced by the URI-encoded `dpId`. It is also the cache key of each `useApiResource` that reads
 * the policy, so it carries no nonce and is stable per data plane. Pure.
 */
export function dpSatelliteRegionPolicyAdminPath(dpId: string): string {
  return DP_SATELLITE_REGION_POLICY_ADMIN_PATH.replace(':id', encodeURIComponent(dpId));
}

/**
 * The first cloud whose stored list or effective list in `answer` holds a region outside the region
 * form of that cloud ({@link satelliteRegionFits}), or `null` when every region fits. Pure.
 */
function policyRegionFormBreak(answer: DpSatelliteRegionPolicyResponse): SatelliteProvider | null {
  for (const cloud of SATELLITE_PROVIDERS) {
    const names = [...(answer[cloud] ?? []), ...answer.effective[cloud]];
    if (names.some((region) => !satelliteRegionFits(cloud, region))) return cloud;
  }
  return null;
}

/**
 * The parsed and checked policy answer: `dpSatelliteRegionPolicyResponseSchema`, then the region form of
 * each cloud. Throws the schema's `ZodError` (its message masked by `answerOf`) or
 * {@link SatelliteRegionFormError} naming the first cloud that breaks its form.
 */
function policyAnswerOf(body: unknown): DpSatelliteRegionPolicyResponse {
  const answer = answerOf(dpSatelliteRegionPolicyResponseSchema.safeParse(body));
  const unfit = policyRegionFormBreak(answer);
  if (unfit !== null) throw new SatelliteRegionFormError(unfit);
  return answer;
}

/**
 * The satellite region policy of `dpId` (contract 4, D-29, R-044): for each cloud, the stored list or
 * `null` (every region of that cloud), and the effective lists where a new satellite may run now.
 *
 * Callers: the Satellite regions card, which shows the policy and edits it, and the satellite setup,
 * which offers only the regions of the effective list of the chosen cloud.
 *
 * The body is parsed with `dpSatelliteRegionPolicyResponseSchema`, so a repeated region, regions out of
 * order, an effective region outside a stored list or an extra key rejects with a `ZodError`, its
 * message masked by `maskCredentialShapes`. A region of either list of a cloud outside the region form of
 * that cloud rejects with {@link SatelliteRegionFormError}, so no such name reaches a label. An HTTP
 * failure rejects with the client's `ApiError`, its server text masked: 400 for an id the strict pipe
 * refuses, 403 without `cp:dp:read`, 404 for an unknown data plane.
 *
 * Abort, as {@link getDpSatelliteJob} has it: a signal that is already aborted rejects with its reason and
 * issues no request, and a signal that aborts later rejects the read with its reason at once. A call with
 * no signal (the card, the setup, the editor's check before its write) is bounded by
 * {@link SATELLITE_READ_TIMEOUT_MS} and rejects with {@link SatelliteReadTimeoutError} when the bound ends
 * it, so a stalled read ends in an error state with Try again (review rows WS-29-R45, WS-29-R72).
 */
export async function getDpSatelliteRegionPolicy(
  dpId: string,
  init: Pick<RequestInit, 'signal'> = {},
): Promise<DpSatelliteRegionPolicyResponse> {
  if (init.signal?.aborted === true) throw init.signal.reason;
  const path = dpSatelliteRegionPolicyAdminPath(dpId);
  return policyAnswerOf(await boundedRead(path, init));
}

/**
 * Replace the satellite region policy of `dpId` with `body` (contract 4, D-29, R-044). The body states the
 * whole policy: each cloud's ticked regions, or `null` for every region of that cloud.
 *
 * Caller: the Save of the Satellite regions card's editor, its only write.
 *
 * Resolves with the policy after the write, parsed and checked as {@link getDpSatelliteRegionPolicy}
 * parses and checks it. Rejects with `ApiError` for any HTTP failure, its server text masked:
 * - 400 for a body the strict pipe refuses, or a ticked region outside the satellite region list of its
 *   cloud;
 * - 403 without `cp:dp:update`;
 * - 404 for an unknown data plane;
 * - 409 for a policy that unticks a region where the data plane holds a satellite on that cloud. The
 *   stored policy stays as it was.
 *
 * A write that has not answered in {@link SATELLITE_WRITE_TIMEOUT_MS} rejects with
 * {@link SatelliteWriteTimeoutError}; `satelliteWriteOutcomeUnknown` tells the caller when the write may
 * still have applied.
 *
 * The answer must store the body: each cloud's stored list equal to the body's list, once each and
 * ascending, `null` kept, the form provisioning-svc stores. An answer that parses and stores other lists
 * rejects with {@link SatelliteAnswerMismatchError} (`member: 'policy'`), whose `asked` and `answered`
 * name the two policies (review row WS-29-R73). `satelliteWriteOutcomeUnknown` reads that rejection as an
 * unknown outcome, so the card reads the policy again.
 */
export async function putDpSatelliteRegionPolicy(
  dpId: string,
  body: PutDpSatelliteRegionPolicyRequest,
): Promise<DpSatelliteRegionPolicyResponse> {
  const answer = policyAnswerOf(await boundedWrite(dpSatelliteRegionPolicyAdminPath(dpId), 'PUT', body));
  const asked = policyText(body.gcp === null ? null : ascendingDistinct(body.gcp), body.aws === null ? null : ascendingDistinct(body.aws));
  const answered = policyText(answer.gcp, answer.aws);
  if (asked !== answered) throw new SatelliteAnswerMismatchError('policy', asked, answered);
  return answer;
}

/** `list` once each, ascending in code-unit order, the order every stored policy list keeps. Pure. */
function ascendingDistinct(list: readonly string[]): string[] {
  return [...new Set(list)].sort((a, b) => (a < b ? -1 : 1));
}

/** The two stored lists of a policy as one text, `gcp: every region; aws: us-east-1`. Pure. */
function policyText(gcp: readonly string[] | null, aws: readonly string[] | null): string {
  const words = (list: readonly string[] | null): string => (list === null ? 'every region' : list.length === 0 ? 'no region' : list.join(', '));
  return `gcp: ${words(gcp)}; aws: ${words(aws)}`;
}
